/**
 * FeatureSelectionStep — Step 1 of setup wizard.
 *
 * Shows available features (subgraphs) from the backend `availableFeatures` query.
 * Features are cross-industry capabilities: payroll, accounting, healthcare, etc.
 * Products are shown as optional bundles that pre-select features + regulatory overlays.
 *
 * Data-driven: feature data from GraphQL (subgraph metadata configs).
 */

import { useMemo } from 'react';
import {
  Stack,
  Group,
  Text,
  Paper,
  SimpleGrid,
  Badge,
  Skeleton,
  Alert,
  Checkbox,
  ThemeIcon,
  Tooltip,
  Accordion,
  Divider,
} from '@mantine/core';
import {
  IconApps,
  IconAlertCircle,
  IconCheck,
  IconPackage,
  IconShieldCheck,
  IconLock,
} from '@tabler/icons-react';
import {
  useFeatureDefinitions,
  type FeatureDefinition,
} from '../hooks/useFeatureDefinitions';
import {
  useProductDefinitions,
  type ProductDefinition,
} from '../hooks/useProductDefinitions';

export type { ProductDefinition };

export interface FeatureSelectionStepProps {
  selectedFeatureIds: string[];
  selectedProductId: string | null;
  onFeaturesChange: (featureIds: string[]) => void;
  onProductSelect: (product: ProductDefinition | null) => void;
}

/** Group features by category for display. */
function groupByCategory(features: FeatureDefinition[]): Record<string, FeatureDefinition[]> {
  const groups: Record<string, FeatureDefinition[]> = {};
  for (const f of features) {
    const cat = f.category || 'other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(f);
  }
  return groups;
}

const CATEGORY_LABELS: Record<string, string> = {
  infrastructure: 'Infrastructure',
  operations: 'Operations',
  healthcare: 'Healthcare',
  other: 'Other',
};

const CATEGORY_ORDER = ['infrastructure', 'operations', 'healthcare', 'other'];

function categoryColor(category: string): string {
  switch (category) {
    case 'infrastructure': return 'gray';
    case 'operations': return 'blue';
    case 'healthcare': return 'red';
    default: return 'gray';
  }
}

export function FeatureSelectionStep({
  selectedFeatureIds,
  selectedProductId,
  onFeaturesChange,
  onProductSelect,
}: FeatureSelectionStepProps) {
  const { features, loading, error } = useFeatureDefinitions();
  const { products, loading: productsLoading } = useProductDefinitions({});
  const activeProducts = products.filter((p) => p.status === 'active');

  const grouped = useMemo(() => groupByCategory(features), [features]);

  // Resolve feature dependencies when toggling
  const handleToggleFeature = (featureId: string, checked: boolean) => {
    const feature = features.find((f) => f.featureId === featureId);
    if (!feature || feature.isCore) return; // Cannot toggle core

    if (checked) {
      // Add feature + all dependencies
      const toAdd = new Set([featureId, ...feature.dependencies]);
      // Recursively resolve dependency chains
      for (const depId of toAdd) {
        const dep = features.find((f) => f.featureId === depId);
        if (dep) {
          for (const d of dep.dependencies) toAdd.add(d);
        }
      }
      const next = new Set([...selectedFeatureIds, ...toAdd]);
      onFeaturesChange(Array.from(next));
    } else {
      // Remove feature — also remove features that depend on it
      const toRemove = new Set([featureId]);
      for (const f of features) {
        if (f.dependencies.includes(featureId)) {
          toRemove.add(f.featureId);
        }
      }
      onFeaturesChange(selectedFeatureIds.filter((id) => !toRemove.has(id)));
    }
  };

  // Apply product bundle — pre-select features from the product's subgraphs
  const handleApplyBundle = (product: ProductDefinition) => {
    if (selectedProductId === product.productId) {
      // Deselect bundle
      onProductSelect(null);
      return;
    }
    onProductSelect(product);
    // Pre-select features from the product's subgraphs
    const bundledFeatures = new Set([...selectedFeatureIds, ...product.subgraphs]);
    // Also add core features
    for (const f of features) {
      if (f.isCore) bundledFeatures.add(f.featureId);
    }
    onFeaturesChange(Array.from(bundledFeatures));
  };

  // Auto-include core features
  const coreFeatureIds = useMemo(
    () => features.filter((f) => f.isCore).map((f) => f.featureId),
    [features],
  );
  const allSelected = useMemo(
    () => new Set([...selectedFeatureIds, ...coreFeatureIds]),
    [selectedFeatureIds, coreFeatureIds],
  );

  const nonCoreSelected = selectedFeatureIds.filter(
    (id) => !coreFeatureIds.includes(id),
  );

  return (
    <Stack gap="md">
      {/* Header */}
      <Group gap="xs">
        <ThemeIcon variant="light" size="lg" color="blue">
          <IconApps size={20} />
        </ThemeIcon>
        <div>
          <Text fw={600} size="lg">Select Features</Text>
          <Text size="sm" c="dimmed">
            Choose the capabilities your business needs. Core engine is always included.
          </Text>
        </div>
      </Group>

      {/* Count badge */}
      {nonCoreSelected.length > 0 && (
        <Badge variant="light" size="lg" color="blue">
          {nonCoreSelected.length} feature{nonCoreSelected.length !== 1 ? 's' : ''} selected
        </Badge>
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Failed to load features: {error.message}
        </Alert>
      )}

      {/* Feature Grid by Category */}
      {loading ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} height={140} radius="md" />
          ))}
        </SimpleGrid>
      ) : (
        <Stack gap="lg">
          {CATEGORY_ORDER.map((category) => {
            const categoryFeatures = grouped[category];
            if (!categoryFeatures || categoryFeatures.length === 0) return null;
            return (
              <div key={category}>
                <Group gap="xs" mb="sm">
                  <Badge variant="light" color={categoryColor(category)} size="sm">
                    {CATEGORY_LABELS[category] ?? category}
                  </Badge>
                </Group>
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                  {categoryFeatures.map((feature) => {
                    const isSelected = allSelected.has(feature.featureId);
                    const isCore = feature.isCore;
                    return (
                      <Paper
                        key={feature.featureId}
                        p="md"
                        withBorder
                        shadow={isSelected ? 'sm' : 'xs'}
                        style={{
                          cursor: isCore ? 'default' : 'pointer',
                          borderColor: isSelected
                            ? `var(--mantine-color-${feature.color}-5)`
                            : undefined,
                          borderWidth: isSelected ? 2 : 1,
                          opacity: isCore ? 0.85 : 1,
                          transition: 'all 150ms ease',
                        }}
                        onClick={() => {
                          if (!isCore) handleToggleFeature(feature.featureId, !isSelected);
                        }}
                      >
                        <Stack gap="xs">
                          <Group justify="space-between" wrap="nowrap">
                            <Group gap="xs" wrap="nowrap">
                              {isCore ? (
                                <Tooltip label="Always enabled">
                                  <ThemeIcon size="sm" color="gray" variant="light" radius="xl">
                                    <IconLock size={12} />
                                  </ThemeIcon>
                                </Tooltip>
                              ) : (
                                <Checkbox
                                  checked={isSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleToggleFeature(feature.featureId, e.currentTarget.checked);
                                  }}
                                  size="sm"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              )}
                              <Text fw={600} size="sm" truncate>
                                {feature.name}
                              </Text>
                            </Group>
                            {isSelected && !isCore && (
                              <ThemeIcon size="sm" color={feature.color} variant="filled" radius="xl">
                                <IconCheck size={12} />
                              </ThemeIcon>
                            )}
                          </Group>

                          <Text size="xs" c="dimmed" lineClamp={2}>
                            {feature.shortDescription || feature.description}
                          </Text>

                          {/* Badges */}
                          <Group gap={4} wrap="wrap">
                            {feature.isLifeCritical && (
                              <Badge size="xs" color="red" variant="filled">
                                Life-Critical
                              </Badge>
                            )}
                            {feature.complianceFrameworks.slice(0, 3).map((fw) => (
                              <Badge key={fw} size="xs" variant="light" color="gray">
                                {fw}
                              </Badge>
                            ))}
                            {feature.complianceFrameworks.length > 3 && (
                              <Badge size="xs" variant="light" color="gray">
                                +{feature.complianceFrameworks.length - 3}
                              </Badge>
                            )}
                          </Group>

                          {/* Dependencies note */}
                          {feature.dependencies.length > 0 && !isCore && (
                            <Text size="xs" c="dimmed" fs="italic">
                              Requires: {feature.dependencies.join(', ')}
                            </Text>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </SimpleGrid>
              </div>
            );
          })}
        </Stack>
      )}

      <Divider />

      {/* Product Bundles (secondary) */}
      <Accordion variant="contained" radius="md">
        <Accordion.Item value="bundles">
          <Accordion.Control icon={<IconPackage size={18} />}>
            <Group gap="xs">
              <Text size="sm" fw={500}>Quick Start: Apply a Product Bundle</Text>
              {activeProducts.length > 0 && (
                <Badge size="xs" variant="light" color="gray">
                  {activeProducts.length} available
                </Badge>
              )}
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Text size="xs" c="dimmed" mb="sm">
              Product bundles pre-select features and add industry-specific regulatory overlays.
            </Text>
            {productsLoading ? (
              <Skeleton height={80} radius="md" />
            ) : activeProducts.length === 0 ? (
              <Text size="sm" c="dimmed">No product bundles available.</Text>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                {activeProducts.map((product) => {
                  const isApplied = selectedProductId === product.productId;
                  return (
                    <Paper
                      key={product.productId}
                      p="sm"
                      withBorder
                      shadow={isApplied ? 'sm' : 'xs'}
                      style={{
                        cursor: 'pointer',
                        borderColor: isApplied ? 'var(--mantine-color-violet-5)' : undefined,
                        borderWidth: isApplied ? 2 : 1,
                        transition: 'all 150ms ease',
                      }}
                      onClick={() => handleApplyBundle(product)}
                    >
                      <Group justify="space-between" wrap="nowrap" mb={4}>
                        <Text fw={600} size="sm" truncate>
                          {product.name}
                        </Text>
                        {isApplied && (
                          <ThemeIcon size="sm" color="violet" variant="filled" radius="xl">
                            <IconCheck size={12} />
                          </ThemeIcon>
                        )}
                      </Group>
                      {product.description && (
                        <Text size="xs" c="dimmed" lineClamp={1} mb={4}>
                          {product.description}
                        </Text>
                      )}
                      <Group gap={4}>
                        <Badge size="xs" variant="light" color="blue">
                          {product.subgraphs.length} features
                        </Badge>
                        <Badge size="xs" variant="light" color="red">
                          {product.requiredRulesets.length} rulesets
                        </Badge>
                        <Tooltip label="Regulatory compliance bundle">
                          <Badge size="xs" leftSection={<IconShieldCheck size={10} />} variant="light" color="grape">
                            Regulatory
                          </Badge>
                        </Tooltip>
                      </Group>
                    </Paper>
                  );
                })}
              </SimpleGrid>
            )}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}
